import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import { LANGUAGES } from '../constants/languages';
import { FindingCard, SEVERITY_ORDER } from '../components/qa/Findings';
import './SharedReportPage.css';

function langName(code) {
  return LANGUAGES[code]?.name || code;
}

export default function SharedReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  // Keep this page out of search indexes in the markup, not just in words -
  // the recipient is often NDA-bound and won't tolerate their file's findings
  // being crawlable. Paired with a robots.txt Disallow for non-JS crawlers.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .getSharedQaReport(token)
      .then((data) => alive && setReport(data))
      .catch((e) =>
        alive &&
        setError(
          e.response?.status === 404
            ? 'This report link is invalid or has been removed.'
            : 'Could not load this report. Please try again later.',
        ),
      );
    return () => {
      alive = false;
    };
  }, [token]);

  if (error) {
    return <div className="shared-report-state">{error}</div>;
  }
  if (!report) {
    return <div className="shared-report-state">Loading report…</div>;
  }

  const findings = report.findings || [];
  const groups = {};
  findings.forEach((f, i) => {
    (groups[f.severity] = groups[f.severity] || []).push({ f, id: i });
  });

  return (
    <div className="shared-report">
      <div className="shared-report-head">
        <h1 className="shared-report-title">Here's what we found in your file</h1>
        <p className="shared-report-sub">
          {langName(report.source_lang)} → {langName(report.target_lang)} ·{' '}
          {report.segments_validated} segments checked · {findings.length}{' '}
          {findings.length === 1 ? 'finding' : 'findings'}
        </p>
        {findings.length > 0 && (
          <div className="shared-report-chips">
            {SEVERITY_ORDER.filter((sev) => report.summary_by_severity?.[sev]).map(
              (sev) => (
                <span key={sev} className={`shared-report-chip ${sev}`}>
                  {sev}
                  <span className="n">{report.summary_by_severity[sev]}</span>
                </span>
              ),
            )}
          </div>
        )}
        <p className="shared-report-privacy">
          Private, unguessable link · not indexed by search engines · auto-deleted after 30 days.
          We store the findings from this QA check (which may quote flagged snippets), not your uploaded file.
        </p>
      </div>

      {findings.length === 0 ? (
        <div className="shared-report-clean">No issues found in this document.</div>
      ) : (
        SEVERITY_ORDER.filter((sev) => groups[sev]).map((sev) => (
          <div key={sev} className="shared-report-group">
            <p className="shared-report-group-head">
              {sev} · {groups[sev].length}
            </p>
            {groups[sev].map((pair) => (
              <FindingCard key={pair.id} finding={pair.f} findingId={pair.id} />
            ))}
          </div>
        ))
      )}
    </div>
  );
}
